// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import { enableMapSet } from "immer";

import auth from "./authSlice";
import common from "./commonSlice/common";
import jobDescriptionReducer from "./jobDescriptionSlice/jobDescription";
import jobRequisitionReducer from "./jobRequisitionSlice/jobRequisition";
import vacancyReducer from "./vacancySlice/vacancy";
import employeeReducer from "./employeeSlice/employee";
import jdDocReducer from "./jobDescriptionDocumentContentSlice/jobDescriptionDocumentContent";
import candidateReducer from "./candidateSlice/candidate";
import interviewReducer from "./interviewSlice/interview";
import appConfigSlice from './configSlice/config'
import offerLetterReducer from './offerLetterSlice/offerLetter'

enableMapSet();

export const store = configureStore({
  reducer: {
    auth: auth,
    common: common,
    jobDescription: jobDescriptionReducer,
    jobRequisition: jobRequisitionReducer,
    vacancy: vacancyReducer,
    candidate: candidateReducer,
    jdDoc: jdDocReducer,
    employee: employeeReducer,
    interview: interviewReducer,
    appConfigSlice,
    offerLetter: offerLetterReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: undefined,
    }),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
